import debounce from 'debounce-promise';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IThirdPartyDataOption, Targetable } from '@feathr/blackbox';
import { toast, UniversalSelect } from '@feathr/components';
import { useAccount, useFlags } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT } from '@feathr/hooks';

import { DataOption, DataSingleValue } from './AffinityTargetingDataSelect.Option';

interface IProps {
  targetable: Targetable;
}

function AffinityTargetingDataSelect({ targetable }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const categoryId = targetable.get('category_id');
  const { showAllAffinityAudiences } = useFlags();
  const account = useAccount();

  function getDataOptionValue({ ThirdPartyDataId }: IThirdPartyDataOption): string {
    return ThirdPartyDataId;
  }

  function handleDataSelect({
    Name,
    ThirdPartyDataId,
    BrandName,
    Description,
    ActiveIDsCount,
    FullPath,
  }: IThirdPartyDataOption): void {
    targetable.set({
      audience_inactive: false,
      name: Name,
      thirdparty_data_id: ThirdPartyDataId,
      thirdparty_data_name: Name,
      thirdparty_brand_name: BrandName,
      description: Description,
      unique_user_count: ActiveIDsCount,
      full_path: FullPath,
    });
  }

  const loadOptions = useCallback(
    async (
      inputValue: string,
      _options: readonly IThirdPartyDataOption[],
      { page }: { page: number } = { page: 0 },
    ): Promise<{
      options: readonly IThirdPartyDataOption[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      try {
        const options = await targetable.getPaginatedThirdPartyData({
          inputValue,
          page,
          pageSize: UniversalSelect.DEFAULT_PAGE_SIZE,
          // Allow internal accounts and users with the flag to see all affinity audiences
          showAllAffinityAudiences: showAllAffinityAudiences || account.isInternal,
        });
        return {
          options,
          hasMore: options.length > 0 && options.length >= UniversalSelect.DEFAULT_PAGE_SIZE,
          additional: {
            page: page + 1,
          },
        };
      } catch (error) {
        toast(
          t('Something went wrong while retrieving affinity audiences:\n{{- error}}', { error }),
          {
            type: 'error',
          },
        );

        return UniversalSelect.EMPTY_PAGINATION;
      }
    },
    [targetable, t, categoryId],
  );

  const debouncedLoadOptions = useMemo(
    () => debounce(loadOptions, DEFAULT_DEBOUNCE_WAIT),
    [loadOptions],
  );

  const validateAudience = useCallback((): string | undefined => {
    return !targetable.isAttributeDirty('thirdparty_data_id') && targetable.get('audience_inactive')
      ? t('Affinity audience unavailable. You must select a different audience.')
      : undefined;
  }, [t, targetable]);

  const value = targetable.get('thirdparty_data_id')
    ? ({
        ThirdPartyDataId: targetable.get('thirdparty_data_id'),
        Name: targetable.get('thirdparty_data_name'),
        ActiveIDsCount: targetable.get('unique_user_count'),
        FullPath: targetable.get('full_path'),
        Description: targetable.get('description'),
      } as IThirdPartyDataOption)
    : undefined;

  const handleClear = useCallback(() => {
    targetable.set({
      audience_inactive: false,
      name: undefined,
      thirdparty_data_id: undefined,
      thirdparty_data_name: undefined,
      thirdparty_brand_name: undefined,
      description: undefined,
      unique_user_count: undefined,
      full_path: undefined,
    });
  }, [targetable]);

  return (
    <UniversalSelect<IThirdPartyDataOption>
      cacheOptions={true}
      closeMenuOnScroll={true}
      components={{ Option: DataOption, SingleValue: DataSingleValue }}
      defaultOptions={true}
      getOptionValue={getDataOptionValue}
      helpText={t('Search for and target an affinity audience.')}
      isClearable={true}
      key={categoryId}
      label={t('Affinity audience')}
      loadPagedOptions={debouncedLoadOptions}
      menuPortalTarget={document.body}
      name={'affinity_audience_select'}
      onClear={handleClear}
      onSelectSingle={handleDataSelect}
      paginate={true}
      placeholder={t('Choose an affinity audience')}
      required={true}
      validationError={validateAudience()}
      value={value}
    />
  );
}

export default observer(AffinityTargetingDataSelect);
